import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Eye, FileText, Pencil, Baby, ScrollText, Archive, ArchiveRestore } from 'lucide-react';
import { enfantsApi } from '../../utils/api';
import FicheEquipe from '../../components/FicheEquipe/FicheEquipe';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { getStatutBadgeClass, getStatutLabel } from '../../utils/calculs';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function EnfantsList() {
  const { getApiParams, structures } = useApp();
  const { user } = useAuth();
  const canManage = ['admin', 'direction'].includes(user?.role);
  const [enfants, setEnfants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterStructure, setFilterStructure] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [ficheId, setFicheId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {
      ...getApiParams(),
      ...(search && { search }),
      ...(filterStatut && { statut: filterStatut }),
      ...(filterStructure && { structure_id: filterStructure }),
      ...(showArchived && { archive: '1' }),
    };
    enfantsApi.getAll(params)
      .then(res => setEnfants(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getApiParams, search, filterStatut, filterStructure, showArchived]);

  const toggleArchive = async (e, enfant) => {
    e.preventDefault(); e.stopPropagation();
    const archiver = !showArchived;
    const verbe = archiver ? 'Archiver' : 'Réactiver';
    if (!window.confirm(`${verbe} ${enfant.prenom} ${enfant.nom} ?`)) return;
    try { await enfantsApi.setArchive(enfant.id, archiver); load(); }
    catch { window.alert('Action impossible.'); }
  };

  useEffect(() => { load(); }, [load]);

  const formatDate = (d) => {
    if (!d) return '—';
    try { return format(parseISO(d), 'd MMM yyyy', { locale: fr }); }
    catch { return d; }
  };

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-52"
            />
          </div>
          {/* Statut filter */}
          <select
            value={filterStatut}
            onChange={e => setFilterStatut(e.target.value)}
            className="form-select w-40 text-sm"
          >
            <option value="">Tous les statuts</option>
            <option value="en_attente">En attente</option>
            <option value="actif">Actif</option>
            <option value="parti">Parti</option>
          </select>
          {/* Structure filter */}
          <select
            value={filterStructure}
            onChange={e => setFilterStructure(e.target.value)}
            className="form-select w-52 text-sm"
          >
            <option value="">Toutes les structures</option>
            {structures.map(s => (
              <option key={s.id} value={s.id}>{s.nom}</option>
            ))}
          </select>
          {/* Actifs / Archivés */}
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-sm">
            <button
              onClick={() => setShowArchived(false)}
              className={`px-3 py-2 ${!showArchived ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >Actifs</button>
            <button
              onClick={() => setShowArchived(true)}
              className={`px-3 py-2 border-l border-gray-200 ${showArchived ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >Archivés</button>
          </div>
        </div>
        {canManage && (
          <Link to="/enfants/nouveau" className="btn-primary">
            <Plus size={16} /> Nouvel enfant
          </Link>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400">Chargement...</div>
        ) : enfants.length === 0 ? (
          <div className="py-16 text-center">
            <Baby size={40} className="text-gray-300 mx-auto mb-3" />
            <div className="text-gray-400 text-sm">Aucun enfant trouvé</div>
            {canManage && (
              <Link to="/enfants/nouveau" className="btn-primary mt-4 inline-flex">
                <Plus size={16} /> Ajouter un enfant
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Enfant</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Naissance</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Structure</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Parent</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Statut</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {enfants.map((e, i) => (
                  <tr
                    key={e.id}
                    className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/enfants/${e.id}`}
                        className="font-medium text-gray-900 hover:text-primary-600 hover:underline"
                        title="Ouvrir la fiche"
                      >
                        {e.prenom} {e.nom}
                      </Link>
                      {e.pai ? (
                        <span className="ml-2 text-xs text-orange-600 font-medium">PAI</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(e.date_naissance)}</td>
                    <td className="px-4 py-3">
                      <span
                        className="text-xs font-medium px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: e.structure_couleur || '#64748b' }}
                      >
                        {e.structure_nom || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {e.parent1_prenom} {e.parent1_nom}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {e.parent1_tel && <div>{e.parent1_tel}</div>}
                      {e.parent1_email && (
                        <div className="text-xs truncate max-w-[140px]">{e.parent1_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={getStatutBadgeClass(e.statut)}>
                        {getStatutLabel(e.statut)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setFicheId(e.id)}
                          className="p-1.5 rounded hover:bg-gold-50 text-gray-500 hover:text-gold-600"
                          title="Fiche de transmission (équipe)"
                        >
                          <ScrollText size={16} />
                        </button>
                        <Link
                          to={`/enfants/${e.id}`}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary-500"
                          title="Voir"
                        >
                          <Eye size={16} />
                        </Link>
                        {canManage && (
                          <Link
                            to={`/devis/nouveau?enfant_id=${e.id}`}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-brand-500"
                            title="Nouveau devis"
                          >
                            <FileText size={16} />
                          </Link>
                        )}
                        {canManage && (
                          <Link
                            to={`/enfants/${e.id}/modifier`}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                            title="Modifier"
                          >
                            <Pencil size={16} />
                          </Link>
                        )}
                        {canManage && (
                          <button
                            onClick={(ev) => toggleArchive(ev, e)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-amber-600"
                            title={showArchived ? 'Réactiver' : 'Archiver'}
                          >
                            {showArchived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && (
        <div className="text-xs text-gray-400">
          {enfants.length} enfant{enfants.length > 1 ? 's' : ''} trouvé{enfants.length > 1 ? 's' : ''}
        </div>
      )}

      {ficheId && <FicheEquipe enfantId={ficheId} onClose={() => setFicheId(null)} />}
    </div>
  );
}
